import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, X, Rocket } from 'lucide-react';
import { Button } from './ui/button';
import { useChannel } from '../context/ChannelContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

export const ONBOARDING_KEY_PREFIX = 'onboarding_dismissed:';
export const dismissKey = (wsId) => `${ONBOARDING_KEY_PREFIX}${wsId}`;

export function clearOnboardingDismissals() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(ONBOARDING_KEY_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {}
}

/**
 * Three-step onboarding checklist shown on the Menus page to workspace admins
 * until every step is done or the card is dismissed for this workspace.
 */
export default function GettingStarted({ menus = [] }) {
  const navigate = useNavigate();
  const { channels } = useChannel();
  const { selectedWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  const { isSuperAdmin } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return !!localStorage.getItem(dismissKey(selectedWorkspaceId)); } catch { return false; }
  });

  if (!selectedWorkspaceId || (!isWorkspaceAdmin && !isSuperAdmin) || dismissed) return null;

  const steps = [
    {
      id: 'channel', title: 'Add a LINE channel',
      desc: 'Paste your Messaging API channel access token.',
      done: channels.length > 0, cta: 'Add channel',
      go: () => navigate('/channels', { state: { openAddChannel: true } }),
    },
    {
      id: 'menu', title: 'Build your first menu',
      desc: 'Upload an image and draw tap areas.',
      done: menus.length > 0, cta: 'Create menu',
      go: () => navigate('/create'),
    },
    {
      id: 'publish', title: 'Publish and set as default',
      desc: 'Push it to LINE and make it the default menu for your followers.',
      done: menus.some((m) => m.isDefault), cta: 'Open menus',
      go: () => navigate('/menus'),
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const next = steps.find((s) => !s.done);

  const dismiss = () => {
    try { localStorage.setItem(dismissKey(selectedWorkspaceId), '1'); } catch {}
    setDismissed(true);
  };

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 sm:p-5 mb-6" data-testid="getting-started">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-[#00E05E]" />
          <h2 className="font-semibold">Getting started</h2>
          <span className="text-xs text-[#52525B]" data-testid="getting-started-progress">{doneCount}/{steps.length} done</span>
        </div>
        <button onClick={dismiss} className="text-[#52525B] hover:text-[#A1A1AA]" aria-label="Dismiss" data-testid="dismiss-onboarding">
          <X className="w-4 h-4" />
        </button>
      </div>
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={s.id} className="flex items-start gap-3">
            {s.done
              ? <CheckCircle2 className="w-5 h-5 text-[#00E05E] flex-shrink-0 mt-0.5" />
              : <Circle className="w-5 h-5 text-[#3F3F46] flex-shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium', s.done && 'line-through text-[#71717A]')}>{i + 1}. {s.title}</p>
              {!s.done && <p className="text-xs text-[#A1A1AA]">{s.desc}</p>}
            </div>
            {!s.done && s === next && (
              <Button size="sm" className="btn-primary" onClick={s.go} data-testid={`onboarding-cta-${s.id}`}>{s.cta}</Button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
